import { useMutation } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { OctagonAlert } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { api, ApiError } from '@/lib/axios'
import { Toast } from '../components/Toast'

type ValidateUserApplicationResponse = {
  applicationId: string
  userId: string
  email: string
  displayName: string
  name: string
  documentNumber: string
  externalUserId: string | null
  role: string
  isNewUser: boolean
}

type ValidateUserApplicationProps = {
  applicationId: string
  userId: string
}

async function validateUserApplicationRequest({
  applicationId,
  userId,
}: ValidateUserApplicationProps) {
  const { data } = await api.backend.get<ValidateUserApplicationResponse>(
    `/application-account-users/validate-user-application/${applicationId}/${userId}`,
  )
  return data
}

export function useValidateApplicationAccountUser() {
  const { t } = useTranslation('common')

  const { mutateAsync, isPending } = useMutation<
    ValidateUserApplicationResponse,
    AxiosError<ApiError>,
    ValidateUserApplicationProps
  >({
    mutationKey: ['/application-account-users/validate-user-application'],
    mutationFn: validateUserApplicationRequest,
    onError: (error) => {
      toast(Toast, {
        data: {
          type: 'danger',
          icon: OctagonAlert,
          title: t('app_connection.errors.validation_error'),
          description:
            error?.response?.data?.friendlyMessage ||
            t('app_connection.errors.validation_error'),
        },
      })
    },
  })

  const handleValidateApplicationAccountUser = async (
    data: ValidateUserApplicationProps,
  ) => {
    return mutateAsync(data)
  }

  return { handleValidateApplicationAccountUser, isValidating: isPending }
}
